import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  ButtonToolbar,
  Checkbox,
  Form,
  IconButton,
  Input,
  Message,
  Panel,
  SelectPicker,
  Stack,
  Table,
  Tag
} from 'rsuite'
import LegacyDownloadIcon from '@rsuite/icons/legacy/Download'
import LegacyRefreshIcon from '@rsuite/icons/legacy/Refresh'

import CatalogTopMenu from '@components/catalog/CatalogTopMenu'
import ProductsFilter from '@components/catalog/product/grid/ProductsFilter'
import MultipleShopPicker from '@components/common/commonFormInput/MultipleShopPicker'
import AppLoader from '@components/common/AppLoader'

import { usePartner } from '@@partner:helpers/hooks/auth'
import { notifyError, notifyOk, makeGraphQLErrorNotification } from '@@partner:helpers/notification'
import {
  buildExportFiltersFromNestedFilter,
  formatTaskDateTime,
  resolveTaskDownloadUrl,
  resolveTaskExportFileName,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  toBackendAbsoluteUrl
} from '@@partner:helpers/exportProducts'

import { useCreateBackgroundTask } from '@@partner:graphql/mutation/backgroundTask/createBackgroundTask.gql'
import {
  BackgroundTaskItem,
  useLazyQueryBackgroundTasks
} from '@@partner:graphql/query/backgroundTask/allBackgroundTasks.gql'

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

type ProductExportField =
  | 'id'
  | 'xid'
  | 'name'
  | 'status'
  | 'slug'
  | 'brand_name'
  | 'category_ids'
  | 'price'
  | 'discount_price'
  | 'old_price'
  | 'vat_code'
  | 'stock_total'
  | 'ignore_stock'
  | 'eans'
  | 'has_photo'
  | 'photos_count'
  | 'preview_id'
  | 'created_at'
  | 'updated_at'

type TaskStatusFilter = 'all' | 'pending' | 'processing' | 'completed' | 'failed'

interface ExportFieldGroup {
  title: string
  fields: Array<{ value: ProductExportField, label: string }>
}

const EXPORT_FIELD_GROUPS: ExportFieldGroup[] = [
  {
    title: 'Основные',
    fields: [
      { value: 'id', label: 'ID' },
      { value: 'xid', label: 'XID' },
      { value: 'name', label: 'Название' },
      { value: 'slug', label: 'Slug' },
      { value: 'status', label: 'Статус' },
      { value: 'brand_name', label: 'Бренд' },
      { value: 'category_ids', label: 'Категории' }
    ]
  },
  {
    title: 'Цены',
    fields: [
      { value: 'price', label: 'Цена' },
      { value: 'discount_price', label: 'Цена со скидкой' },
      { value: 'old_price', label: 'Старая цена' },
      { value: 'vat_code', label: 'НДС' }
    ]
  },
  {
    title: 'Остатки и идентификаторы',
    fields: [
      { value: 'stock_total', label: 'Остаток' },
      { value: 'ignore_stock', label: 'Игнор остатка' },
      { value: 'eans', label: 'Штрих-коды' }
    ]
  },
  {
    title: 'Фото',
    fields: [
      { value: 'has_photo', label: 'Есть фото' },
      { value: 'photos_count', label: 'Кол-во фото' },
      { value: 'preview_id', label: 'Preview ID' }
    ]
  },
  {
    title: 'Даты',
    fields: [
      { value: 'created_at', label: 'Создан' },
      { value: 'updated_at', label: 'Обновлен' }
    ]
  }
]

const ALL_EXPORT_FIELDS: ProductExportField[] = EXPORT_FIELD_GROUPS.flatMap(
  (group) => group.fields.map((field) => field.value)
)

const DEFAULT_EXPORT_FIELDS: ProductExportField[] = [
  'id',
  'xid',
  'name',
  'status',
  'price',
  'discount_price',
  'old_price',
  'stock_total',
  'eans',
  'has_photo',
  'photos_count',
  'preview_id',
  'created_at',
  'updated_at'
]

const TASK_STATUS_OPTIONS: Array<{ label: string, value: TaskStatusFilter }> = [
  { label: 'Все статусы', value: 'all' },
  { label: 'В очереди', value: 'pending' },
  { label: 'В работе', value: 'processing' },
  { label: 'Завершено', value: 'completed' },
  { label: 'Ошибка', value: 'failed' }
]

const TASKS_PAGE_SIZE = 50
const TASKS_POLL_INTERVAL_MS = 5000

const ACTIVE_TASK_STATUSES = new Set([ 'pending', 'processing' ])
const TERMINAL_TASK_STATUSES = new Set([ 'completed', 'failed' ])

const INITIAL_FILTER: NestedFilter = {
  fields: [ 'id' ],
  op: 'unwrap'
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

export default function CatalogExportPage () {
  const { currentBrand } = usePartner()

  const [ filter, setFilter ] = useState<NestedFilter>(INITIAL_FILTER)
  const [ selectedFields, setSelectedFields ] = useState<ProductExportField[]>(DEFAULT_EXPORT_FIELDS)
  const [ selectedShopIds, setSelectedShopIds ] = useState<GQL_ID[]>([])
  const [ notificationEmail, setNotificationEmail ] = useState('')

  const [ tasksStatusFilter, setTasksStatusFilter ] = useState<TaskStatusFilter>('all')

  const [ createBackgroundTask, { loading: exportLoading } ] = useCreateBackgroundTask()

  const [
    fetchBackgroundTasks,
    {
      data: backgroundTasksData,
      loading: backgroundTasksLoading,
      error: backgroundTasksError,
      refetch: refetchBackgroundTasks
    }
  ] = useLazyQueryBackgroundTasks()

  const loadBackgroundTasks = useCallback(() => {
    return fetchBackgroundTasks({
      variables: {
        filter: {
          type: 'export_products',
          ...(tasksStatusFilter !== 'all' ? { status: tasksStatusFilter } : {})
        },
        page: 1,
        pageSize: TASKS_PAGE_SIZE
      }
    })
  }, [ fetchBackgroundTasks, tasksStatusFilter ])

  useEffect(() => {
    void loadBackgroundTasks()
  }, [ loadBackgroundTasks ])

  const handleRefreshTasks = useCallback(() => {
    if (refetchBackgroundTasks) {
      void refetchBackgroundTasks({
        filter: {
          type: 'export_products',
          ...(tasksStatusFilter !== 'all' ? { status: tasksStatusFilter } : {})
        },
        page: 1,
        pageSize: TASKS_PAGE_SIZE
      })
      return
    }

    void loadBackgroundTasks()
  }, [ loadBackgroundTasks, refetchBackgroundTasks, tasksStatusFilter ])

  const handleToggleField = useCallback((field: ProductExportField, checked: boolean) => {
    setSelectedFields((prev) => {
      if (checked) {
        if (prev.includes(field)) {
          return prev
        }
        return [ ...prev, field ]
      }
      return prev.filter((item) => item !== field)
    })
  }, [])

  const handleToggleGroup = useCallback((group: ExportFieldGroup, allChecked: boolean) => {
    setSelectedFields((prev) => {
      const groupValues = group.fields.map((field) => field.value)
      if (allChecked) {
        return prev.filter((item) => !groupValues.includes(item))
      }
      const next = new Set(prev)
      groupValues.forEach((value) => next.add(value))
      return Array.from(next)
    })
  }, [])

  const handleStartExport = useCallback(async () => {
    const brandId = Number(currentBrand?.id)

    if (!Number.isInteger(brandId) || brandId <= 0) {
      notifyError('Не удалось запустить экспорт', 'Не найден текущий бренд')
      return
    }

    if (selectedFields.length === 0) {
      notifyError('Не удалось запустить экспорт', 'Выберите хотя бы одно поле')
      return
    }

    const trimmedEmail = notificationEmail.trim()
    if (trimmedEmail.length > 0 && !isValidEmail(trimmedEmail)) {
      notifyError('Не удалось запустить экспорт', 'Некорректный email')
      return
    }

    const filters = buildExportFiltersFromNestedFilter(filter)
    const normalizedShopIds = selectedShopIds
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)

    if (normalizedShopIds.length > 0) {
      filters.shopIds = normalizedShopIds
    }

    try {
      await createBackgroundTask({
        variables: {
          input: {
            type: 'export_products',
            payload: {
              brandId,
              fields: selectedFields,
              filters,
              filter
            },
            ...(trimmedEmail.length > 0 ? { notification_email: trimmedEmail } : {})
          }
        }
      })

      notifyOk('Экспорт запущен', 'Задача добавлена в очередь')
      void loadBackgroundTasks()
    }
    catch (error) {
      makeGraphQLErrorNotification('Ошибка запуска экспорта')(error as any)
    }
  }, [
    createBackgroundTask,
    currentBrand?.id,
    filter,
    loadBackgroundTasks,
    notificationEmail,
    selectedFields,
    selectedShopIds
  ])

  const handleResetSettings = useCallback(() => {
    setSelectedFields(DEFAULT_EXPORT_FIELDS)
    setSelectedShopIds([])
    setNotificationEmail('')
  }, [])

  const tasks = useMemo<BackgroundTaskItem[]>(
    () => backgroundTasksData?.allBackgroundTasks?.items ?? [],
    [ backgroundTasksData ]
  )
  const totalTasks = backgroundTasksData?.allBackgroundTasks?.total ?? tasks.length

  const hasActiveTasks = tasks.some((task) => ACTIVE_TASK_STATUSES.has(task.status))

  const previousStatusesRef = useRef<Record<number, string>>({})

  useEffect(() => {
    const previous = previousStatusesRef.current
    const next: Record<number, string> = {}

    tasks.forEach((task) => {
      const wasActive = ACTIVE_TASK_STATUSES.has(previous[task.id])
      const becameTerminal = TERMINAL_TASK_STATUSES.has(task.status)

      if (wasActive && becameTerminal) {
        if (task.status === 'completed') {
          const fileName = resolveTaskExportFileName(task)
          const downloadUrl = resolveTaskDownloadUrl(task, fileName)
          notifyOk(
            `Экспорт #${task.id} готов`,
            downloadUrl ? (
              <a href={toBackendAbsoluteUrl(downloadUrl)} target="_blank" rel="noreferrer">
                Скачать {fileName}
              </a>
            ) : fileName
          )
        }
        else {
          notifyError(`Экспорт #${task.id} завершился с ошибкой`)
        }
      }

      next[task.id] = task.status
    })

    previousStatusesRef.current = next
  }, [ tasks ])

  useEffect(() => {
    if (!hasActiveTasks) {
      return
    }

    const interval = window.setInterval(() => {
      void loadBackgroundTasks()
    }, TASKS_POLL_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [ hasActiveTasks, loadBackgroundTasks ])

  const isExportDisabled = !Number.isInteger(Number(currentBrand?.id)) || selectedFields.length === 0

  const selectedFieldsCount = selectedFields.length

  const fieldGroupsState = useMemo(() => {
    return EXPORT_FIELD_GROUPS.map((group) => {
      const groupValues = group.fields.map((field) => field.value)
      const checkedCount = groupValues.filter((value) => selectedFields.includes(value)).length
      return {
        group,
        checkedCount,
        totalCount: groupValues.length,
        allChecked: checkedCount === groupValues.length,
        someChecked: checkedCount > 0 && checkedCount < groupValues.length
      }
    })
  }, [ selectedFields ])

  return (
    <div className="CatalogExportPage">
      <h1 className="u-heading">Экспорт каталога</h1>
      <CatalogTopMenu />

      <Panel
        bordered
        collapsible
        defaultExpanded
        header="Фильтры товаров"
        style={{ marginTop: 16 }}
      >
        <ProductsFilter
          onFilterChanged={setFilter}
          showBulkUpdate={false}
          onShowBulkUpdate={() => {}}
          canBulkUpdate={false}
        />
      </Panel>

      <Panel
        bordered
        collapsible
        defaultExpanded
        header={`Поля для экспорта (${selectedFieldsCount}/${ALL_EXPORT_FIELDS.length})`}
        style={{ marginTop: 16 }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 16
          }}
        >
          {fieldGroupsState.map(({ group, allChecked, someChecked, checkedCount, totalCount }) => (
            <div key={group.title}>
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={() => handleToggleGroup(group, allChecked)}
              >
                <strong>{group.title}</strong>
                <span style={{ color: '#8e8e93', marginLeft: 6, fontWeight: 'normal' }}>
                  {checkedCount}/{totalCount}
                </span>
              </Checkbox>
              <div style={{ paddingLeft: 24 }}>
                {group.fields.map((field) => (
                  <Checkbox
                    key={field.value}
                    checked={selectedFields.includes(field.value)}
                    onChange={(_value, checked) => handleToggleField(field.value, checked)}
                  >
                    {field.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          ))}
        </div>

        <ButtonToolbar style={{ marginTop: 12 }}>
          <Button size="xs" appearance="ghost" onClick={() => setSelectedFields(ALL_EXPORT_FIELDS)}>
            Выбрать все
          </Button>
          <Button size="xs" appearance="ghost" onClick={() => setSelectedFields(DEFAULT_EXPORT_FIELDS)}>
            По умолчанию
          </Button>
          <Button size="xs" appearance="ghost" onClick={() => setSelectedFields([])}>
            Очистить
          </Button>
        </ButtonToolbar>
      </Panel>

      <Panel
        bordered
        collapsible
        defaultExpanded
        header="Параметры запуска"
        style={{ marginTop: 16 }}
      >
        <Form fluid>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <MultipleShopPicker
              value={selectedShopIds}
              onChange={setSelectedShopIds}
              helpText="Если не выбраны, экспорт пойдёт по всем магазинам бренда"
            />

            <Form.Group>
              <Form.ControlLabel>Email для уведомления</Form.ControlLabel>
              <Input
                value={notificationEmail}
                onChange={setNotificationEmail}
                placeholder="email@example.com"
                type="email"
              />
              <Form.HelpText>Уведомление придёт после завершения задачи. Можно оставить пустым.</Form.HelpText>
            </Form.Group>
          </div>
        </Form>

        <ButtonToolbar style={{ marginTop: 16 }}>
          <Button
            appearance="primary"
            color="green"
            size="lg"
            loading={exportLoading}
            onClick={handleStartExport}
            disabled={isExportDisabled}
          >
            Запустить экспорт
          </Button>
          <Button appearance="ghost" size="lg" onClick={handleResetSettings}>
            Сбросить настройки
          </Button>
        </ButtonToolbar>
      </Panel>

      <Panel
        bordered
        header={`Задачи экспорта (${totalTasks})`}
        style={{ marginTop: 16 }}
      >
        <Stack spacing={8} style={{ marginBottom: 12 }}>
          <SelectPicker
            data={TASK_STATUS_OPTIONS}
            value={tasksStatusFilter}
            cleanable={false}
            searchable={false}
            onChange={(value) => setTasksStatusFilter((value || 'all') as TaskStatusFilter)}
            style={{ width: 220 }}
          />
          <IconButton
            icon={<LegacyRefreshIcon />}
            onClick={handleRefreshTasks}
            loading={backgroundTasksLoading}
            appearance="ghost"
          >
            Обновить
          </IconButton>
        </Stack>

        {backgroundTasksError && (
          <Message showIcon type="error">
            Не удалось загрузить задачи экспорта
          </Message>
        )}

        {!backgroundTasksError && (
          <Table
            data={tasks}
            autoHeight
            wordWrap
            loading={backgroundTasksLoading}
            renderLoading={() => <AppLoader.Inner content="Загрузка задач" />}
            renderEmpty={() => (
              <div style={{ padding: 16, textAlign: 'center', color: '#8e8e93' }}>
                Нет задач для выбранного статуса
              </div>
            )}
          >
            <Column width={80} verticalAlign="middle">
              <HeaderCell>ID</HeaderCell>
              <Cell>{(task: BackgroundTaskItem) => `#${task.id}`}</Cell>
            </Column>

            <Column width={140} verticalAlign="middle">
              <HeaderCell>Статус</HeaderCell>
              <Cell>
                {(task: BackgroundTaskItem) => (
                  <Tag color={TASK_STATUS_COLORS[task.status]}>
                    {TASK_STATUS_LABELS[task.status] ?? task.status}
                  </Tag>
                )}
              </Cell>
            </Column>

            <Column flexGrow={1} minWidth={160} verticalAlign="middle">
              <HeaderCell>Создана</HeaderCell>
              <Cell>{(task: BackgroundTaskItem) => formatTaskDateTime(task.created_at)}</Cell>
            </Column>

            <Column flexGrow={1} minWidth={160} verticalAlign="middle">
              <HeaderCell>Завершена</HeaderCell>
              <Cell>{(task: BackgroundTaskItem) => formatTaskDateTime(task.completed_at)}</Cell>
            </Column>

            <Column flexGrow={1} minWidth={160} verticalAlign="middle">
              <HeaderCell>Прогресс</HeaderCell>
              <Cell>
                {(task: BackgroundTaskItem) => {
                  const stats = (task.result as Record<string, any> | null | undefined)?.stats
                  if (!stats) {
                    return <span style={{ color: '#8e8e93' }}>—</span>
                  }
                  const exported = stats.exported ?? 0
                  const total = stats.totalProducts ?? 0
                  const errors = typeof stats.errors === 'number' ? stats.errors : 0
                  return (
                    <span>
                      {exported}/{total}
                      {errors > 0 && (
                        <span style={{ color: '#e74c3c', marginLeft: 6 }}>· ошибок: {errors}</span>
                      )}
                    </span>
                  )
                }}
              </Cell>
            </Column>

            <Column flexGrow={2} minWidth={220} verticalAlign="middle">
              <HeaderCell>Файл</HeaderCell>
              <Cell>
                {(task: BackgroundTaskItem) => {
                  const fileName = resolveTaskExportFileName(task)
                  return <span style={{ wordBreak: 'break-all' }}>{fileName}</span>
                }}
              </Cell>
            </Column>

            <Column width={140} fixed="right" verticalAlign="middle">
              <HeaderCell>Действия</HeaderCell>
              <Cell>
                {(task: BackgroundTaskItem) => {
                  const fileName = resolveTaskExportFileName(task)
                  const downloadUrl = resolveTaskDownloadUrl(task, fileName)

                  if (!downloadUrl || task.status !== 'completed') {
                    return <span style={{ color: '#8e8e93' }}>—</span>
                  }

                  return (
                    <IconButton
                      as="a"
                      href={toBackendAbsoluteUrl(downloadUrl)}
                      target="_blank"
                      rel="noreferrer"
                      icon={<LegacyDownloadIcon />}
                      appearance="primary"
                      size="xs"
                    >
                      Скачать
                    </IconButton>
                  )
                }}
              </Cell>
            </Column>
          </Table>
        )}
      </Panel>
    </div>
  )
}
